/**
 * Générateur — Automatisme : lire un tableau, un diagramme, une courbe
 * Attendu ID : 1036 | Niveau : 6ème | Domaine : OGD
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève sait lire un tableau,
 * un diagramme en barres, un diagramme circulaire ou une courbe dans des cas
 * adaptés à une lecture immédiate. »
 * Labels (cartes exerciseur) :
 *   - Lire un tableau ou un diagramme
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * LECTURE IMMÉDIATE seulement : on PRÉLÈVE une donnée, on ne calcule jamais
 * (pas de totaux, pas d'écarts, pas de comparaisons multiplicatives).
 * QUATRE supports : barres (valeurs sur les graduations ou à mi-cran),
 * tableau (simple ou double entrée), circulaire (la nouveauté CM2 — jamais
 * de pourcentages : la catégorie la plus/moins représentée, la moitié),
 * courbe (le point ET la tendance : « entre quelles heures ça baisse »).
 * Les trois réflexes du cadrage sont servis : titre + étiquettes toujours
 * présents, graduation repérable, croisement.
 * Le solveur test-side RELIT LES FIGURES (hauteurs de barres ramenées au
 * pas de graduation, cellules, angles des secteurs, points de la courbe),
 * jamais l'énoncé.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie,
} from '../../../utils';

const ATTENDU_ID = 1036;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Lire un tableau ou un diagramme';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1036 : ${message}`);
};

const dataUrl = (svg) => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const elImage = (url) => ({ version: 1, elements: [{ type: 'image', config: { imageUrl: url } }] });

const BLEU = '#1E3A8A';
const GRIS = '#64748b';
const PALETTE = ['#1E3A8A', '#F97316', '#0d9488', '#f59e0b'];

// ─── Les thèmes (titre + étiquettes : le réflexe n° 1) ────────────────────

const THEMES = [
    {
        titre: 'Livres empruntés au CDI',
        cats: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
        question: (c) => `Combien de livres ont été empruntés le ${c} ?`,
        lignes: ['classe de 6eA', 'classe de 6eB'],
        questionDouble: (l, c) => `Combien de livres la ${l.replace('classe de ', '')} a-t-elle empruntés le ${c} ?`,
    },
    {
        titre: 'Fruits vendus au marché',
        cats: ['pommes', 'poires', 'bananes', 'fraises', 'cerises'],
        question: (c) => `Combien de ${c} ont été vendues ?`,
    },
    {
        titre: 'Élèves inscrits par activité',
        cats: ['judo', 'danse', 'natation', 'escalade', 'théâtre'],
        question: (c) => `Combien d'élèves sont inscrits en ${c} ?`,
    },
    {
        titre: 'Vélos loués à la base de loisirs',
        cats: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'],
        question: (c) => `Combien de vélos ont été loués le ${c} ?`,
        lignes: ['groupe A', 'groupe B'],
        questionDouble: (l, c) => `Combien de vélos le ${l} a-t-il loués le ${c} ?`,
    },
];

// ─── Support 1 : diagramme en barres ──────────────────────────────────────

const PX_PAR_CRAN = 32;

/** Barres : axe gradué (data-valaxe = étiquettes d'axe, data-cat sur les
 *  rects) — les valeurs sont sur les graduations, ou à mi-cran. */
const svgBarres = (theme, valeurs, pas) => {
    const X0 = 52;
    const BASE = 196;
    const H = 5 * PX_PAR_CRAN;
    const p = [];
    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 240" width="300" height="212" role="img"');
    p.push(`  aria-label="Diagramme en barres : ${theme.titre}. ${theme.cats.map((c, i) => `${c} : ${valeurs[i]}`).join(', ')}.">`);
    p.push(`  <text x="170" y="18" font-size="13" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${theme.titre}</text>`);
    for (let k = 0; k <= 5; k++) {
        const y = BASE - k * PX_PAR_CRAN;
        p.push(`  <line x1="${X0}" y1="${y}" x2="330" y2="${y}" stroke="${k === 0 ? '#334155' : '#cbd5e1'}" stroke-width="${k === 0 ? 1.6 : 1}"/>`);
        p.push(`  <text x="${X0 - 6}" y="${y + 4}" font-size="10.5" fill="${GRIS}" text-anchor="end" font-family="system-ui,sans-serif" data-valaxe="${k * pas}">${k * pas}</text>`);
    }
    theme.cats.forEach((c, i) => {
        const h = (valeurs[i] / pas) * PX_PAR_CRAN;
        const x = X0 + 10 + i * 54;
        p.push(`  <rect x="${x}" y="${(BASE - h).toFixed(1)}" width="34" height="${h.toFixed(1)}" fill="${BLEU}" data-cat="${c}"/>`);
        p.push(`  <text x="${x + 17}" y="${BASE + 15}" font-size="10" font-weight="600" fill="#111827" text-anchor="middle" font-family="system-ui,sans-serif">${c}</text>`);
    });
    p.push(`  <line x1="${X0}" y1="${BASE - H - 6}" x2="${X0}" y2="${BASE}" stroke="#334155" stroke-width="1.6"/>`);
    p.push('</svg>');
    return p.join('\n');
};

/** Tirage barres : pas 5 ou 10, valeurs sur cran (ou mi-cran ~25 %), la
 *  catégorie interrogée a une valeur DISTINCTE de ses voisines. */
const tirerBarres = () => {
    for (let essai = 0; essai < 60; essai++) {
        const theme = randomChoice(THEMES);
        const pas = randomChoice([5, 10]);
        const valeurs = theme.cats.map(() => {
            const k = randInt(1, 5);
            return Math.random() < 0.25 && k < 5 ? k * pas + pas / 2 : k * pas;
        });
        const idx = randInt(0, theme.cats.length - 1);
        const voisins = [valeurs[idx - 1], valeurs[idx + 1]].filter((v) => v !== undefined);
        if (voisins.some((v) => v === valeurs[idx])) continue;
        garde(valeurs.every((v) => v > 0 && v <= 5 * pas), 'valeur hors axe');
        return { theme, valeurs, pas, idx };
    }
    const theme = THEMES[0];
    return { theme, valeurs: [10, 15, 30, 20, 25], pas: 10, idx: 1 };
};

const finirQcm = (bonne, dis, texte, sousType, elements) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        { ...opts(L1), ...(elements ? { elements_additionnels: elements } : {}) },
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

const genBarresQcm = () => {
    const { theme, valeurs, pas, idx } = tirerBarres();
    const bonne = String(valeurs[idx]);
    const candidats = [];
    // L'ERREUR CIBLE : la barre voisine
    const voisins = [valeurs[idx - 1], valeurs[idx + 1]].filter((v) => v !== undefined);
    candidats.push({ valeur: String(randomChoice(voisins)), code: 'barre_voisine' });
    candidats.push({ valeur: String(valeurs[idx] + pas), code: 'graduation_dessus' });
    candidats.push({ valeur: String(Math.max(0, valeurs[idx] - pas)), code: 'graduation_dessous' });
    candidats.push({ valeur: String(valeurs[idx] + pas / 2), code: 'demi_cran' });
    // vivier de secours : les autres barres (jamais de bouche-trou invente)
    valeurs.forEach((v, i2) => { if (i2 !== idx) candidats.push({ valeur: String(v), code: 'autre_barre' }); });
    const vus = new Set([bonne]);
    const dis = candidats.filter((c) => {
        if (vus.has(c.valeur) || c.valeur === '0') return false;
        vus.add(c.valeur);
        return true;
    }).slice(0, 3);
    return finirQcm(bonne, dis,
        `<p><b>${theme.question(theme.cats[idx])}</b></p><p>{{element:0}}</p>`,
        'barres', elImage(dataUrl(svgBarres(theme, valeurs, pas))));
};

const genBarresSaisie = () => {
    const { theme, valeurs, pas, idx } = tirerBarres();
    const payload = makeSaisie(
        String(valeurs[idx]),
        `<p><b>${theme.question(theme.cats[idx])}</b></p><p>{{element:0}}</p>`,
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svgBarres(theme, valeurs, pas))) },
    );
    payload._meta.params = { sousType: 'barres_saisie' };
    return payload;
};

// ─── Support 2 : tableau (simple ou double entrée) ────────────────────────

/** Tableau SVG : cellules portées par des <text data-l data-c>. */
const svgTableau = (theme, lignes, colonnes, donnees) => {
    const X0 = 10;
    const LW = 92;                       // largeur de la colonne d'en-têtes de ligne
    const CW = Math.min(52, (330 - X0 - LW) / colonnes.length);
    const RH = 26;
    const nb = lignes.length;
    const p = [];
    const H = 34 + (nb + 1) * RH + 8;
    p.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 ${H}" width="310" height="${Math.round(H * 310 / 340)}" role="img"`);
    p.push(`  aria-label="Tableau : ${theme.titre}.">`);
    p.push(`  <text x="170" y="18" font-size="13" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${theme.titre}</text>`);
    const top = 28;
    // grille
    for (let r = 0; r <= nb + 1; r++) {
        p.push(`  <line x1="${X0}" y1="${top + r * RH}" x2="${X0 + LW + colonnes.length * CW}" y2="${top + r * RH}" stroke="#94a3b8" stroke-width="1"/>`);
    }
    for (let c = 0; c <= colonnes.length; c++) {
        const x = X0 + (c === 0 ? 0 : LW + c * CW);
        p.push(`  <line x1="${x}" y1="${top}" x2="${x}" y2="${top + (nb + 1) * RH}" stroke="#94a3b8" stroke-width="1"/>`);
    }
    p.push(`  <line x1="${X0 + LW}" y1="${top}" x2="${X0 + LW}" y2="${top + (nb + 1) * RH}" stroke="#94a3b8" stroke-width="1"/>`);
    // en-têtes de colonnes
    colonnes.forEach((c, j) => {
        p.push(`  <text x="${X0 + LW + j * CW + CW / 2}" y="${top + 17}" font-size="10" font-weight="700" fill="#111827" text-anchor="middle" font-family="system-ui,sans-serif">${c}</text>`);
    });
    // lignes
    lignes.forEach((l, i) => {
        const y = top + (i + 1) * RH + 17;
        p.push(`  <text x="${X0 + 6}" y="${y}" font-size="10" font-weight="700" fill="#111827" text-anchor="start" font-family="system-ui,sans-serif">${l}</text>`);
        colonnes.forEach((c, j) => {
            p.push(`  <text x="${X0 + LW + j * CW + CW / 2}" y="${y}" font-size="11" font-weight="600" fill="#111827" text-anchor="middle" font-family="system-ui,sans-serif" data-l="${l}" data-c="${c}">${donnees[i][j]}</text>`);
        });
    });
    p.push('</svg>');
    return p.join('\n');
};

const tirerTableau = () => {
    for (let essai = 0; essai < 60; essai++) {
        const doubleEntree = Math.random() < 0.5;
        const theme = randomChoice(doubleEntree ? THEMES.filter((t) => t.lignes) : THEMES);
        const colonnes = theme.cats.slice(0, doubleEntree ? 4 : 5);
        const lignes = doubleEntree ? theme.lignes : ['nombre'];
        const donnees = lignes.map(() => colonnes.map(() => randInt(5, 49)));
        const i = randInt(0, lignes.length - 1);
        const j = randInt(0, colonnes.length - 1);
        // la cellule interrogée est UNIQUE dans le tableau (pas d'ambiguïté)
        const cible = donnees[i][j];
        const toutes = donnees.flat();
        if (toutes.filter((v) => v === cible).length !== 1) continue;
        const question = doubleEntree
            ? theme.questionDouble(lignes[i], colonnes[j])
            : theme.question(colonnes[j]);
        return { theme, lignes, colonnes, donnees, i, j, question, doubleEntree };
    }
    const theme = THEMES[0];
    return {
        theme, lignes: ['nombre'], colonnes: theme.cats, i: 0, j: 1,
        donnees: [[12, 15, 31, 24, 18]], question: theme.question('mardi'), doubleEntree: false,
    };
};

const genTableauQcm = () => {
    const t = tirerTableau();
    const bonne = String(t.donnees[t.i][t.j]);
    // PRIORITÉ aux cases voisines (l'erreur cible : le mauvais croisement) —
    // le vivier ne sert QU'EN COMPLÉMENT, jamais en concurrence (revue
    // 2026-08-22 : un shuffle global noyait les pièges nommés à 17 %)
    const voisines = [];
    if (t.donnees[t.i][t.j - 1] !== undefined) voisines.push({ valeur: String(t.donnees[t.i][t.j - 1]), code: 'colonne_voisine' });
    if (t.donnees[t.i][t.j + 1] !== undefined) voisines.push({ valeur: String(t.donnees[t.i][t.j + 1]), code: 'colonne_voisine' });
    if (t.doubleEntree) voisines.push({ valeur: String(t.donnees[1 - t.i][t.j]), code: 'ligne_voisine' });
    const candidats = [
        ...shuffle(voisines),
        { valeur: String(t.donnees[t.i][t.j] + 1), code: 'lecture_imprecise' },
        // vivier de secours : les autres cases (si des voisines se doublonnent)
        ...shuffle(t.donnees.flat().map((v) => ({ valeur: String(v), code: 'autre_case' }))),
    ];
    const vus = new Set([bonne]);
    const dis = candidats.filter((c) => {
        if (vus.has(c.valeur)) return false;
        vus.add(c.valeur);
        return true;
    }).slice(0, 3);
    return finirQcm(bonne, dis,
        `<p><b>${t.question}</b></p><p>{{element:0}}</p>`,
        'tableau', elImage(dataUrl(svgTableau(t.theme, t.lignes, t.colonnes, t.donnees))));
};

const genTableauSaisie = () => {
    const t = tirerTableau();
    const payload = makeSaisie(
        String(t.donnees[t.i][t.j]),
        `<p><b>${t.question}</b></p><p>{{element:0}}</p>`,
        { ...opts(L1), elements_additionnels: elImage(dataUrl(svgTableau(t.theme, t.lignes, t.colonnes, t.donnees))) },
    );
    payload._meta.params = { sousType: 'tableau_saisie' };
    return payload;
};

// ─── Support 3 : diagramme circulaire ─────────────────────────────────────

const THEMES_CIRC = [
    { titre: 'Sports préférés de la classe', cats: ['foot', 'danse', 'judo', 'natation'] },
    { titre: 'Animaux préférés de la classe', cats: ['chien', 'chat', 'lapin', 'oiseau'] },
    { titre: 'Desserts choisis à la cantine', cats: ['fruit', 'yaourt', 'gâteau', 'compote'] },
];

const svgCirculaire = (theme, cats, angles) => {
    const CX = 95;
    const CY = 122;
    const R = 74;
    const p = [];
    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 220" width="300" height="194" role="img"');
    p.push(`  aria-label="Diagramme circulaire : ${theme.titre}, ${cats.length} catégories.">`);
    p.push(`  <text x="170" y="18" font-size="13" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${theme.titre}</text>`);
    let a = 0;
    cats.forEach((c, i) => {
        const a2 = a + angles[i];
        const x1 = CX + R * Math.sin((a * Math.PI) / 180);
        const y1 = CY - R * Math.cos((a * Math.PI) / 180);
        const x2 = CX + R * Math.sin((a2 * Math.PI) / 180);
        const y2 = CY - R * Math.cos((a2 * Math.PI) / 180);
        const grand = angles[i] > 180 ? 1 : 0;
        p.push(`  <path d="M ${CX} ${CY} L ${x1.toFixed(1)} ${y1.toFixed(1)} A ${R} ${R} 0 ${grand} 1 ${x2.toFixed(1)} ${y2.toFixed(1)} Z" fill="${PALETTE[i]}" stroke="#ffffff" stroke-width="1.5" data-cat="${c}"/>`);
        a = a2;
    });
    // légende
    cats.forEach((c, i) => {
        const y = 60 + i * 26;
        p.push(`  <rect x="196" y="${y - 11}" width="14" height="14" rx="3" fill="${PALETTE[i]}"/>`);
        p.push(`  <text x="216" y="${y}" font-size="11.5" font-weight="600" fill="#111827" text-anchor="start" font-family="system-ui,sans-serif">${c}</text>`);
    });
    p.push('</svg>');
    return p.join('\n');
};

/** Angles : max unique avec ≥ 30° d'avance, min unique avec ≥ 20° de
 *  retard (lecture immédiate garantie). Mode « moitié » : un secteur à 180°. */
const tirerCirculaire = (avecMoitie) => {
    for (let essai = 0; essai < 200; essai++) {
        const theme = randomChoice(THEMES_CIRC);
        const cats = shuffle([...theme.cats]);
        let angles;
        if (avecMoitie) {
            const reste = [randInt(30, 90), randInt(30, 90)];
            const dernier = 180 - reste[0] - reste[1];
            if (dernier < 25) continue;
            angles = shuffle([180, reste[0], reste[1], dernier]);
        } else {
            const bruts = [randInt(15, 150), randInt(15, 150), randInt(15, 150), randInt(15, 150)];
            const somme = bruts.reduce((s, x) => s + x, 0);
            angles = bruts.map((x) => Math.round((x * 360) / somme));
            angles[3] = 360 - angles[0] - angles[1] - angles[2];
        }
        const tries = [...angles].sort((a, b) => b - a);
        if (angles.some((x) => x < 18)) continue;
        if (tries[0] - tries[1] < 30) continue;                  // max net
        if (tries[tries.length - 2] - tries[tries.length - 1] < 20) continue; // min net
        if (avecMoitie && tries[0] !== 180) continue;
        return { theme, cats, angles };
    }
    return { theme: THEMES_CIRC[0], cats: THEMES_CIRC[0].cats, angles: [180, 80, 60, 40] };
};

const genCirculaireQcm = () => {
    const mode = randomChoice(['plus', 'plus', 'moins', 'moitie']);
    const { theme, cats, angles } = tirerCirculaire(mode === 'moitie');
    let idx;
    let question;
    if (mode === 'plus' || mode === 'moitie') {
        idx = angles.indexOf(Math.max(...angles));
        question = mode === 'moitie'
            ? 'Quelle catégorie représente la moitié du diagramme ?'
            : 'Quelle est la catégorie la plus représentée ?';
    } else {
        idx = angles.indexOf(Math.min(...angles));
        question = 'Quelle est la catégorie la moins représentée ?';
    }
    const dis = cats.filter((c, i) => i !== idx)
        .map((c) => ({ valeur: c, code: 'autre_categorie' }));
    return finirQcm(cats[idx], dis,
        `<p><b>${question}</b></p><p>{{element:0}}</p>`,
        `circulaire_${mode}`, elImage(dataUrl(svgCirculaire(theme, cats, angles))));
};

// ─── Support 4 : la courbe (le point, et la tendance) ─────────────────────

const HEURES = [8, 10, 12, 14, 16, 18];
const PX_CRAN_C = 26;

const svgCourbe = (valeurs, pas) => {
    const X0 = 50;
    const BASE = 190;
    const p = [];
    p.push('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 340 226" width="300" height="199" role="img"');
    p.push(`  aria-label="Courbe : température relevée au fil de la journée, de 8 h à 18 h.">`);
    p.push(`  <text x="170" y="16" font-size="13" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">Température relevée dans la journée</text>`);
    for (let k = 0; k <= 5; k++) {
        const y = BASE - k * PX_CRAN_C;
        p.push(`  <line x1="${X0}" y1="${y}" x2="330" y2="${y}" stroke="${k === 0 ? '#334155' : '#cbd5e1'}" stroke-width="${k === 0 ? 1.6 : 1}"/>`);
        p.push(`  <text x="${X0 - 6}" y="${y + 4}" font-size="10.5" fill="${GRIS}" text-anchor="end" font-family="system-ui,sans-serif" data-valaxe="${k * pas}">${k * pas}${k === 5 ? ' °C' : ''}</text>`);
    }
    const xDe = (h) => X0 + 14 + ((h - 8) / 2) * 51;
    const yDe = (v) => BASE - (v / pas) * PX_CRAN_C;
    const pts = HEURES.map((h, i) => `${xDe(h).toFixed(1)},${yDe(valeurs[i]).toFixed(1)}`).join(' ');
    p.push(`  <polyline points="${pts}" fill="none" stroke="#F97316" stroke-width="2.4"/>`);
    HEURES.forEach((h, i) => {
        p.push(`  <circle cx="${xDe(h).toFixed(1)}" cy="${yDe(valeurs[i]).toFixed(1)}" r="4" fill="#F97316" data-heure="${h}"/>`);
        p.push(`  <text x="${xDe(h).toFixed(1)}" y="${BASE + 15}" font-size="10" font-weight="600" fill="#111827" text-anchor="middle" font-family="system-ui,sans-serif">${h} h</text>`);
    });
    p.push('</svg>');
    return p.join('\n');
};

/** Courbe : un SEUL sommet (montée puis descente) ou un seul creux —
 *  valeurs sur les graduations (pas 2 ou 4), toutes distinctes de leurs
 *  voisines. */
const tirerCourbe = () => {
    // l'extremum est choisi AVANT la boucle de rejet : sinon les sommets
    // tardifs (longue montée) sont presque toujours rejetés et la réponse
    // tendance devient quasi constante (défaut A2 vécu : 51 % de
    // « entre 14 h et 18 h », 6 % de 16 h)
    const pas = randomChoice([2, 4]);
    const sommetIdx = randInt(2, 4);
    const creux = Math.random() < 0.3;
    for (let essai = 0; essai < 200; essai++) {
        // strictement monotone de chaque côté de l'extremum (un seul sommet)
        const crans = [creux ? randInt(4, 5) : randInt(1, 2)];
        for (let i = 1; i < 6; i++) {
            const monte = creux ? i > sommetIdx : i <= sommetIdx;
            crans.push(crans[i - 1] + randInt(1, 2) * (monte ? 1 : -1));
        }
        if (crans.some((x) => x < 1 || x > 5)) continue;
        return { valeurs: crans.map((x) => x * pas), pas, sommetIdx, creux };
    }
    // repli : la rampe canonique vers le sommet choisi (toujours réalisable)
    const crans = [];
    for (let i = 0; i < 6; i++) {
        crans.push(creux
            ? 1 + Math.abs(i - sommetIdx)
            : 5 - Math.abs(i - sommetIdx));
    }
    return { valeurs: crans.map((x) => x * pas), pas, sommetIdx, creux };
};

const genCourbeQcm = () => {
    const { valeurs, pas, sommetIdx, creux } = tirerCourbe();
    const svg = elImage(dataUrl(svgCourbe(valeurs, pas)));
    if (Math.random() < 0.55) {
        // LE POINT : « Quelle température à 12 h ? »
        const i = randInt(0, 5);
        const bonne = String(valeurs[i]);
        const candidats = [];
        if (valeurs[i - 1] !== undefined) candidats.push({ valeur: String(valeurs[i - 1]), code: 'heure_voisine' });
        if (valeurs[i + 1] !== undefined) candidats.push({ valeur: String(valeurs[i + 1]), code: 'heure_voisine' });
        candidats.push({ valeur: String(valeurs[i] + pas), code: 'graduation_dessus' });
        candidats.push({ valeur: String(Math.max(0, valeurs[i] - pas)), code: 'graduation_dessous' });
        // vivier de secours : les autres points de la courbe
        valeurs.forEach((v, i2) => { if (i2 !== i) candidats.push({ valeur: String(v), code: 'autre_point' }); });
        const vus = new Set([bonne]);
        const dis = candidats.filter((c) => {
            if (vus.has(c.valeur) || c.valeur === '0') return false;
            vus.add(c.valeur);
            return true;
        }).slice(0, 3);
        return finirQcm(`${bonne} °C`, dis.map((d) => ({ ...d, valeur: `${d.valeur} °C` })),
            `<p><b>Quelle température a-t-on relevée à ${HEURES[i]} h ?</b></p><p>{{element:0}}</p>`,
            'courbe_point', svg);
    }
    // LA TENDANCE : l'unique intervalle où ça baisse (ou monte, si creux)
    const hSommet = HEURES[sommetIdx];
    const sens = creux ? 'monte' : 'baisse';
    const bonne = creux ? `entre ${hSommet} h et 18 h` : `entre ${hSommet} h et 18 h`;
    // ⚠ jamais de sous-intervalle du bon sens (il serait vrai aussi) : les
    // trois faux contiennent tous un morceau du sens inverse
    const dis = [
        { valeur: `entre 8 h et ${hSommet} h`, code: 'sens_inverse' },
        { valeur: 'entre 8 h et 18 h', code: 'tout_l_intervalle' },
        { valeur: `entre ${HEURES[sommetIdx - 1]} h et ${HEURES[Math.min(5, sommetIdx + 1)]} h`, code: 'a_cheval_sur_l_extremum' },
    ].filter((c, i2, arr) => arr.findIndex((x) => x.valeur === c.valeur) === i2 && c.valeur !== bonne).slice(0, 3);
    return finirQcm(bonne, dis,
        `<p><b>Entre quelles heures la température ${sens}-t-elle ?</b></p><p>{{element:0}}</p>`,
        'courbe_tendance', svg);
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'LECTURE IMMÉDIATE : on prélève, on ne calcule JAMAIS (ni totaux, ni écarts, ni comparaisons)',
        'titre + étiquettes toujours présents ; valeurs sur les graduations (ou mi-cran signalé barres)',
        'quatre supports, dont le circulaire (nouveauté CM2) SANS pourcentages ni angles',
        'le solveur relit les FIGURES (hauteurs/cases/angles/points), jamais l\'énoncé',
    ],
    lecture_barres_qcm: {
        fixes: {
            barre_voisine: 'la valeur de la barre d\'à côté — L\'ERREUR CIBLE (mauvais croisement)',
            graduation_dessus_dessous: '±1 cran de graduation',
            demi_cran: 'le demi-cran au-dessus',
        },
    },
    lecture_tableau_qcm: {
        fixes: {
            colonne_voisine: 'la case d\'à côté — L\'ERREUR CIBLE',
            ligne_voisine: 'l\'autre ligne, même colonne (double entrée)',
            lecture_imprecise: '±1',
        },
        garde: 'la cellule interrogée est UNIQUE dans le tableau',
    },
    lecture_circulaire_qcm: {
        support: 'plus/moins représentée, ou la moitié — les choix sont les 4 catégories',
        garde: 'max net (≥ 30° d\'avance), min net (≥ 20°), aucun secteur < 18°',
    },
    lecture_courbe_qcm: {
        point: 'heure voisine (cible) + ±1 graduation',
        tendance: 'un seul sommet (ou creux) : la réponse est l\'unique intervalle du sens demandé',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    lecture_barres_qcm: genBarresQcm,
    lecture_barres_saisie: genBarresSaisie,
    lecture_tableau_qcm: genTableauQcm,
    lecture_tableau_saisie: genTableauSaisie,
    lecture_circulaire_qcm: genCirculaireQcm,
    lecture_courbe_qcm: genCourbeQcm,
};

const FORMAT_LABELS = {
    lecture_barres_qcm: 'Barres (QCM)',
    lecture_barres_saisie: 'Barres (saisie)',
    lecture_tableau_qcm: 'Tableau (QCM)',
    lecture_tableau_saisie: 'Tableau (saisie)',
    lecture_circulaire_qcm: 'Circulaire',
    lecture_courbe_qcm: 'Courbe',
};

export const CATEGORIES = [
    {
        id: 'lecturedonnees',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
